import { User } from "@auth0/auth0-react";

const LABELS = {
  email: "email",
  page: "page",
  process: "process",
  group: "group",
  schedule: "schedule",
};

const OPERATORS = {
  notEqual: "!=",
  equal: "=",
  contains: "~=",
};

/** allow config like [email=daniel@raventelemetry.com;page=trends] to set true only if the specified conditions are all matched
 *
 * - e.g. "[email=bob@cat.com]" = only true when user has this email
 * - e.g. "[email~=kiosk]" = true when user email contains "kiosk"
 * - e.g. "[page!=trends]" = true on any page except trends
 * - e.g. "[page~=dash]" = true when page name contains "dash", e.g. "dashboard"
 * - e.g. "[process=raveng_d_001,raveng_d_002;page=trends;email=bob@cat.com]" = only true when we're on the trends page for process raveng_d_001 or raveng_d_002 and the user's email is bob@cat.com
 * - e.g. "[process~=raveng]" = true when process id contains "raveng"
 * - e.g. "retrotaggingEnabled": "[email=bob@cat.com,alice@dog.com]" = enable feature only for specific emails
 *
 * refer to handleParseFlightConfigValue.test.tsx for more examples */
export function handleParseFlightConfigValue(
  valueRaw: any,
  {
    pageName,
    user,
    currentProcessId,
    currentGroupId,
    currentWorkScheduleId,
  }: {
    pageName: string;
    user?: User;
    currentProcessId?: string | null;
    currentGroupId?: string | null;
    currentWorkScheduleId?: string | null;
  }
) {
  if (!user) {
    return false;
  }

  // Handle object-based flight config with conditional keys
  if (valueRaw && typeof valueRaw === "object" && !Array.isArray(valueRaw)) {
    // Get all keys from the object
    const keys = Object.keys(valueRaw);

    // Check if any key is a condition string
    for (const key of keys) {
      if (typeof key === "string" && key.startsWith("[") && key.endsWith("]")) {
        // Evaluate the condition using recursive call
        const conditionMatches = handleParseFlightConfigValue(key, {
          pageName,
          user,
          currentProcessId,
          currentGroupId,
          currentWorkScheduleId,
        });

        // If condition is true, return the associated value
        if (conditionMatches === true) {
          return valueRaw[key];
        }
      }
    }

    // If no conditions matched, return the original object
    return valueRaw;
  }

  if (typeof valueRaw !== "string") {
    return valueRaw;
  }

  // Check if it's a customized value (wrapped in square brackets)
  if (!valueRaw.startsWith("[") || !valueRaw.endsWith("]")) {
    return valueRaw; // Not a customization string
  }

  // Strip off the surrounding brackets
  const innerContent = valueRaw.substring(1, valueRaw.length - 1);

  // If empty brackets, return false
  if (!innerContent) {
    return false;
  }

  // Check if any valid label is present
  const containsValidLabel = Object.values(LABELS).some(
    label =>
      innerContent.includes(`${label}=`) ||
      innerContent.includes(`${label} =`) ||
      innerContent.includes(`${label}!=`) ||
      innerContent.includes(`${label} !=`) ||
      innerContent.includes(`${label}~=`) ||
      innerContent.includes(`${label} ~=`)
  );

  if (!containsValidLabel) {
    return false;
  }

  // Split conditions by semicolon
  // Only remove spaces from the condition keys/syntax, not from the values
  const conditions = innerContent.split(";").map(cond =>
    // Normalize whitespace around operators but preserve the rest
    cond.trim().replace(/(\w+)\s*([=!~]=?)\s*/g, "$1$2")
  );

  // Handle each condition
  let email_match = true;
  let page_match = true;
  let process_match = true;
  let group_match = true;
  let schedule_match = true;

  for (const condition of conditions) {
    if (
      condition.startsWith(LABELS.email) ||
      condition.match(/^email\s*[=!~]/)
    ) {
      // Handle email with whitespace around operators
      if (
        condition.startsWith(LABELS.email + OPERATORS.contains) ||
        condition.match(/^email\s*~=/)
      ) {
        // Email contains operator (~=)
        const valueStr = condition
          .replace(/^email\s*~=/, "")
          .replace(/^email~=/, "");
        const searchValues = valueStr.split(",");

        email_match = searchValues.some(term => user?.email?.includes(term));
      } else if (
        condition.startsWith(LABELS.email + OPERATORS.notEqual) ||
        condition.match(/^email\s*!=/)
      ) {
        // Email not equal operator (!=)
        const valueStr = condition
          .replace(/^email\s*!=/, "")
          .replace(/^email!=/, "");
        const emailValues = valueStr.split(",");

        email_match = !emailValues.includes(user?.email || "");
      } else if (
        condition.startsWith(LABELS.email + OPERATORS.equal) ||
        condition.match(/^email\s*=/)
      ) {
        // Email equal operator (=)
        const valueStr = condition
          .replace(/^email\s*=/, "")
          .replace(/^email=/, "");
        const emailValues = valueStr.split(",");

        email_match = emailValues.includes(user?.email || "");
      } else {
        email_match = false;
      }
    } else if (
      condition.startsWith(LABELS.page) ||
      condition.match(/^page\s*[=!~]/)
    ) {
      // Handle page with whitespace around operators
      if (
        condition.startsWith(LABELS.page + OPERATORS.contains) ||
        condition.match(/^page\s*~=/)
      ) {
        // Page contains operator (~=)
        const valueStr = condition
          .replace(/^page\s*~=/, "")
          .replace(/^page~=/, "");
        const searchValues = valueStr.split(",");

        page_match = searchValues.some(term => pageName?.includes(term));
      } else if (
        condition.startsWith(LABELS.page + OPERATORS.notEqual) ||
        condition.match(/^page\s*!=/)
      ) {
        // Page not equal operator (!=)
        const valueStr = condition
          .replace(/^page\s*!=/, "")
          .replace(/^page!=/, "");
        const pageValues = valueStr.split(",");

        page_match = !pageValues.includes(pageName);
      } else if (
        condition.startsWith(LABELS.page + OPERATORS.equal) ||
        condition.match(/^page\s*=/)
      ) {
        // Page equal operator (=)
        const valueStr = condition
          .replace(/^page\s*=/, "")
          .replace(/^page=/, "");
        const pageValues = valueStr.split(",");

        page_match = pageValues.includes(pageName);
      } else {
        page_match = false;
      }
    } else if (
      condition.startsWith(LABELS.process) ||
      condition.match(/^process\s*[=!~]/)
    ) {
      // Handle process with whitespace around operators
      if (
        condition.startsWith(LABELS.process + OPERATORS.contains) ||
        condition.match(/^process\s*~=/)
      ) {
        // Process contains operator (~=)
        const valueStr = condition
          .replace(/^process\s*~=/, "")
          .replace(/^process~=/, "");
        const searchValues = valueStr.split(",");

        process_match = currentProcessId
          ? searchValues.some(term => currentProcessId.includes(term))
          : false;
      } else if (
        condition.startsWith(LABELS.process + OPERATORS.notEqual) ||
        condition.match(/^process\s*!=/)
      ) {
        // Process not equal operator (!=)
        const valueStr = condition
          .replace(/^process\s*!=/, "")
          .replace(/^process!=/, "");
        const processValues = valueStr.split(",");

        process_match = !processValues.includes(currentProcessId || "");
      } else if (
        condition.startsWith(LABELS.process + OPERATORS.equal) ||
        condition.match(/^process\s*=/)
      ) {
        // Process equal operator (=)
        const valueStr = condition
          .replace(/^process\s*=/, "")
          .replace(/^process=/, "");
        const processValues = valueStr.split(",");

        process_match = currentProcessId
          ? processValues.includes(currentProcessId)
          : false;
      } else {
        process_match = false;
      }
    } else if (
      condition.startsWith(LABELS.group) ||
      condition.match(/^group\s*[=!~]/)
    ) {
      // Handle group with whitespace around operators
      if (
        condition.startsWith(LABELS.group + OPERATORS.contains) ||
        condition.match(/^group\s*~=/)
      ) {
        // Group contains operator (~=)
        const valueStr = condition
          .replace(/^group\s*~=/, "")
          .replace(/^group~=/, "");
        const searchValues = valueStr.split(",");

        group_match = currentGroupId
          ? searchValues.some(term => currentGroupId.includes(term))
          : false;
      } else if (
        condition.startsWith(LABELS.group + OPERATORS.notEqual) ||
        condition.match(/^group\s*!=/)
      ) {
        // Group not equal operator (!=)
        const valueStr = condition
          .replace(/^group\s*!=/, "")
          .replace(/^group!=/, "");
        const groupValues = valueStr.split(",");

        group_match = !groupValues.includes(currentGroupId || "");
      } else if (
        condition.startsWith(LABELS.group + OPERATORS.equal) ||
        condition.match(/^group\s*=/)
      ) {
        // Group equal operator (=)
        const valueStr = condition
          .replace(/^group\s*=/, "")
          .replace(/^group=/, "");
        const groupValues = valueStr.split(",");

        group_match = currentGroupId
          ? groupValues.includes(currentGroupId)
          : false;
      } else {
        group_match = false;
      }
    } else if (
      condition.startsWith(LABELS.schedule) ||
      condition.match(/^schedule\s*[=!~]/)
    ) {
      // Handle schedule with whitespace around operators
      if (
        condition.startsWith(LABELS.schedule + OPERATORS.contains) ||
        condition.match(/^schedule\s*~=/)
      ) {
        // Schedule contains operator (~=)
        const valueStr = condition
          .replace(/^schedule\s*~=/, "")
          .replace(/^schedule~=/, "");
        const searchValues = valueStr.split(",");

        schedule_match = currentWorkScheduleId
          ? searchValues.some(term => currentWorkScheduleId.includes(term))
          : false;
      } else if (
        condition.startsWith(LABELS.schedule + OPERATORS.notEqual) ||
        condition.match(/^schedule\s*!=/)
      ) {
        // Schedule not equal operator (!=)
        const valueStr = condition
          .replace(/^schedule\s*!=/, "")
          .replace(/^schedule!=/, "");
        const scheduleValues = valueStr.split(",");

        schedule_match = !scheduleValues.includes(currentWorkScheduleId || "");
      } else if (
        condition.startsWith(LABELS.schedule + OPERATORS.equal) ||
        condition.match(/^schedule\s*=/)
      ) {
        // Schedule equal operator (=)
        const valueStr = condition
          .replace(/^schedule\s*=/, "")
          .replace(/^schedule=/, "");
        const scheduleValues = valueStr.split(",");

        schedule_match = currentWorkScheduleId
          ? scheduleValues.includes(currentWorkScheduleId)
          : false;
      } else {
        schedule_match = false;
      }
    }
  }

  return (
    email_match && page_match && process_match && group_match && schedule_match
  );
}
